/* eslint-disable import/no-duplicates */
import { IdentifierNameModel } from '../api.types';
import { UserRole } from '../authentication/authentication.types';
import {
  BackendIntegrationType,
  CarModelDriveType,
  CarModelFuelType,
  CarModelTransmissionType,
  FuelPolicyType,
  ItemGroupType,
  MileagePolicyType,
  MileageUnitPeriodType,
  MileageUnitType,
  PaymentType
} from '../core/core.types';
import { PricePeriodType } from '../pricing/pricing.types';
import dayjs, { Dayjs } from 'dayjs';

export const reservationManageRoles = [ UserRole.SUPPLIER_MANAGE, UserRole.RESERVATION_MANAGE ];
export const reservationViewRoles = [ UserRole.RESERVATION_VIEW, UserRole.RESERVATION_ACCEPT, UserRole.RESERVATION_CANCEL, UserRole.RESERVATION_MANAGE ];

export interface BrokerRateLog {
  code: string;
  vatRate: number;
  payLocallyMarkup: number;
  paymentType: PaymentType;
  markupRate: number;
}

export interface CarModelLog {
  carModelId: number;
  name: string;
  acrissCode: string;
  imageUrl: string;
  passengerCount: number;
  baggageCountSmall: number;
  baggageCountBig: number;
  transmissionType: CarModelTransmissionType;
  driveType: CarModelDriveType;
  fuelType: CarModelFuelType;
  doorCount: number;
  hasAirConditioning: boolean;
  isGuaranteedModel: boolean;
  hasPrice: boolean;
  features: string[];
}

export interface Charge {
  currency: string;
  type: ChargeTypes;
  amount: number;
}

export interface ChargeLog {
  charges: Charge[];
}

export interface CustomerModel {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  personalId: string;
  cityOfResidence: string;
  address: string;
  gender: Genders;
}

export interface ItemLog {
  coverages: ReservationItemModel[];
  extras: ReservationItemModel[];
  fees: ReservationItemModel[];
}

export interface ProductLog {
  fuelPolicyType: FuelPolicyType;
  mileagePolicyType: MileagePolicyType;
  mileageUnitType: MileageUnitType;
  mileageUnitPeriodType: MileageUnitPeriodType;
  mileageQuantity: number;
}

export interface PromoCodeLog {
  code: string;
  discountPercentage: number;
  price: number;
}

export interface ReservationEditRequest {
  brokerRateCode?: string;
  customerLastName?: string;
  supplierReferenceCode?: string;
  reservationStatusType?: ReservationStatusTypes;
}

export interface ReservationFilters {
  combinedFilterField?: string;
  brokerId?: string;
  brokerRateCode?: string;
  reservationStatus?: string;
  pickupDeskCodes?: string;
  date?: string;
  reservationsBetween: { label: TimeIntervalFilterValues; from?: Dayjs; to?: Dayjs };
  pageNumber?: number;
}

export interface ReservationItemModel {
  otaCode: string;
  key: string;
  name: string;
  maxQuantity: number;
  net: number;
  tax: number;
  price: number;
  currency: string;
  code: string;
  type: ItemGroupType;
  unitPrice: number;
  maxPrice: number;
  unit: PricePeriodType;
  quantity: number;
  oneItemPrice: number;
  excessCurrency: string;
  excessAmount: number;
  depositAmount: number;
  saf: number;
}

export interface LogModel {
  requestTimestamp: string;
  responseTimestamp: string;
  request: string;
  response: string;
  requestType: PaymentRequestType | RequestType;
  paymentGatewayType?: PaymentGatewayType;
  backendIntegrationType?: BackendIntegrationType;
  success: boolean;
}

export interface LogsModel {
  logs: LogModel[];
}

export interface ReservationModel {
  reservationNumber: string;
  rateQuoteUuid: string;
  rateRequestTimestamp: string;
  reservationTimestamp: string;
  pickupDeskCode: string;
  returnDeskCode: string;
  carModelLog: CarModelLog;
  productLog: ProductLog;
  ratePlanId: number;
  pickupTimestamp: string;
  returnTimestamp: string;
  rentalDays: number;
  brokerRateLog: BrokerRateLog;
  sourceCountryCode: string;
  driverAge: number;
  reservationStatusType: ReservationStatusTypes;
  inclusiveItemLog: ItemLog;
  optionalItemLog: ItemLog;
  mandatoryItemLog: ItemLog;
  conditionalItemLog: ItemLog;
  chargeLog: ChargeLog;
  promoCodeLog: PromoCodeLog;
  customer: CustomerModel;
  flightNumber: string;
  airlineIataCode: string;
  transportationType: TransportationTypes;
  brokerReferenceCode: string;
  payNow: boolean;
  locale: any;
  customerSpecialReq: string;
  supplierReferenceCode: string;
  brokerId: number;
  lastPayment: ReservationPaymentModel;
  drivers: Driver[];
  rateQuoteLog: any;
  discountLog: Discount;
  createdBy: string;
  createdAt: string;
  modifiedBy: string;
  modifiedAt: string;
}

export interface Driver {
  id: number;
  firstName: string;
  lastName: string;
  licenseNo: string;
  expiry: string;
  gender: Genders;
  additional: boolean;
}

export interface Discount {
  discounts: DiscountLogModel[];
}

export interface DiscountLogModel {
  id: number;
  discountPercentage: number;
  pickupFrom: string;
  pickupTo: string;
  daysFrom: number;
  daysTo: number;
  applied: boolean;
  carClasses: string[];
  brokerRates: string[];
  products: string[];
  brokers: number[];
  desks: string[];
}

export interface ReservationRequestItemModel {
  code: string;
  quantity?: number;
}

export enum ReservationStatusTypes {
  CONFIRMED = 'CONFIRMED',
  NO_SHOW = 'NO_SHOW',
  CANCELLED = 'CANCELLED',
  REQUEST_DENIED = 'REQUEST_DENIED',
  COMPLETED = 'COMPLETED',
  PENDING = 'PENDING'
}

export enum TimeIntervalFilterValues {
  TODAY = 'TODAY',
  H24 = 'H24',
  YESTERDAY = 'YESTERDAY',
  WEEK = 'WEEK',
  MONTH = 'MONTH',
  LASTMONTH = 'LASTMONTH',
  ALL = 'ALL'
}

export const DATE_FILTER_RANGES: { [key in TimeIntervalFilterValues]: { from: Dayjs; to: Dayjs } | undefined } = {
  [ TimeIntervalFilterValues.TODAY ]: { from: dayjs().startOf('day'), to: dayjs().endOf('day') },
  [ TimeIntervalFilterValues.H24 ]: { from: dayjs().startOf('day').subtract(1, 'day'), to: dayjs() },
  [ TimeIntervalFilterValues.YESTERDAY ]: { from: dayjs().startOf('day').subtract(1, 'day'), to: dayjs() },
  [ TimeIntervalFilterValues.WEEK ]: { from: dayjs().startOf('week'), to: dayjs().endOf('week') },
  [ TimeIntervalFilterValues.MONTH ]: { from: dayjs().startOf('month'), to: dayjs().endOf('month') },
  [ TimeIntervalFilterValues.LASTMONTH ]: {
    from: dayjs().startOf('month').subtract(1, 'month'),
    to: dayjs().endOf('month').subtract(1, 'month')
  },
  [ TimeIntervalFilterValues.ALL ]: undefined
};

export const reservationStatusTypeOptions = [
  { value: ReservationStatusTypes.CONFIRMED, label: 'CONFIRMED' },
  { value: ReservationStatusTypes.NO_SHOW, label: 'NO SHOW' },
  { value: ReservationStatusTypes.CANCELLED, label: 'CANCELLED' },
  { value: ReservationStatusTypes.REQUEST_DENIED, label: 'REQUEST DENIED' },
  { value: ReservationStatusTypes.COMPLETED, label: 'COMPLETED' },
  { value: ReservationStatusTypes.PENDING, label: 'PENDING' }
];

export enum TransportationTypes {
  PLANE = 'PLANE',
  WALK = 'WALK'
}

export enum Genders {
  FEMALE = 'FEMALE',
  MALE = 'MALE'
}

export enum ChargeTypes {
  VEHICLE_BASE_RATE = 'VEHICLE_BASE_RATE',
  BROKER_VEHICLE_BASE_RATE = 'BROKER_VEHICLE_BASE_RATE',
  ADJUSTED_VEHICLE_BASE_RATE_TOTAL = 'ADJUSTED_VEHICLE_BASE_RATE_TOTAL',
  ADJUSTED_VEHICLE_BASE_RATE_NET = 'ADJUSTED_VEHICLE_BASE_RATE_NET',
  ADJUSTED_VEHICLE_BASE_RATE_TAX = 'ADJUSTED_VEHICLE_BASE_RATE_TAX',
  ADJUSTED_VEHICLE_BASE_RATE_SAF = 'ADJUSTED_VEHICLE_BASE_RATE_SAF',
  RATE_TOTAL_AMOUNT = 'RATE_TOTAL_AMOUNT',
  BROKER_COMMISSION_TOTAL = 'BROKER_COMMISSION_TOTAL',
  BROKER_COMMISSION_NET = 'BROKER_COMMISSION_NET',
  BROKER_COMMISSION_TAX = 'BROKER_COMMISSION_NET',
  VEHICLE_RATE = 'BROKER_COMMISSION_NET',
  RATE_TOTAL_AMOUNT_W_COMMISSION_TOTAL = 'RATE_TOTAL_AMOUNT_W_COMMISSION_TOTAL',
  PROMO_CODE_DISCOUNT_TOTAL = 'PROMO_CODE_DISCOUNT_TOTAL',
  PROMO_CODE_DISCOUNT_NET = 'PROMO_CODE_DISCOUNT_NET',
  PROMO_CODE_DISCOUNT_SAF = 'PROMO_CODE_DISCOUNT_SAF',
  PROMO_CODE_DISCOUNT_TAX = 'PROMO_CODE_DISCOUNT_TAX',
  ESTIMATED_TOTAL_AMOUNT = 'ESTIMATED_TOTAL_AMOUNT',
  ESTIMATED_TOTAL_NET = 'ESTIMATED_TOTAL_NET',
  ESTIMATED_TOTAL_TAX = 'ESTIMATED_TOTAL_TAX',
  ESTIMATED_TOTAL_SAF = 'ESTIMATED_TOTAL_SAF',
  BROKER_LIABILITY = 'BROKER_LIABILITY',
  TOTAL_PAY_LOCALLY = 'TOTAL_PAY_LOCALLY',
  COLLECT_NOW_W_COMMISSION_TOTAL = 'COLLECT_NOW_W_COMMISSION_TOTAL',
  COLLECT_NOW = 'COLLECT_NOW',
  NET_REVENUE_TOTAL = 'NET_REVENUE_TOTAL',
  PAY_LOCALLY_FEE_TOTAL = 'PAY_LOCALLY_FEE_TOTAL',
  PAY_LOCALLY_FEE_NET = 'PAY_LOCALLY_FEE_NET',
  PAY_LOCALLY_FEE_TAX = 'PAY_LOCALLY_FEE_TAX',
  TOTAL_TAXES = 'TOTAL_TAXES',
  MANDATORY_ITEM_TOTAL = 'MANDATORY_ITEM_TOTAL',
  MANDATORY_ITEM_NET = 'MANDATORY_ITEM_NET',
  MANDATORY_ITEM_TAX = 'MANDATORY_ITEM_TAX',
  MANDATORY_ITEM_SAF = 'MANDATORY_ITEM_SAF'
}

export interface ReservationDetailViewModel {
  reservation: ReservationModel;
  pickupDesk: IdentifierNameModel<string>;
  returnDesk: IdentifierNameModel<string>;
  ratePlan: IdentifierNameModel<number>;
  brokerRate: IdentifierNameModel<number>;
  broker: IdentifierNameModel<number>;
}

export interface ReservationPaymentModel {
  dateCreated: string;
  transactionId: string;
  transactionStatus: PaymentStatusType;
  pan: string;
  paymentGateway: PaymentGatewayType;
}

export enum PaymentStatusType {
  COLLECTED = 'COLLECTED',
  PREAUTH = 'PREAUTH',
  MPI_TOKEN = 'MPI_TOKEN',
  MPI_TOKEN_FAILED = 'MPI_TOKEN_FAILED',
  MPI_VALIDATED = 'MPI_VALIDATED',
  MPI_VALIDATION_FAILED = 'MPI_VALIDATION_FAILED',
  TOKEN = 'TOKEN',
  CANCELLED = 'CANCELLED',
  CANCELLATION_FAILED = 'CANCELLATION_FAILED',
  COLLECTION_FAILED = 'COLLECTION_FAILED',
  PREAUTH_FAILED = 'PREAUTH_FAILED',
  FAILED = 'FAILED',
  DECLINED = 'DECLINED',
  REFUNDED = 'REFUNDED',
  REFUND_FAILED = 'REFUND_FAILED'
}

export enum RequestType {
  CREATE_RESERVATION = 'CREATE_RESERVATION',
  CANCEL_RESERVATION = 'CANCEL_RESERVATION',
  RETRIEVE_RESERVATION = 'RETRIEVE_RESERVATION',
  ADD_PAYMENT = 'ADD_PAYMENT',
  ADD_DRIVERS = 'ADD_DRIVERS',
  UPDATE_PAYMENT_INFO = 'UPDATE_PAYMENT_INFO',
  UPDATE_DRIVER_LIST = 'UPDATE_DRIVER_LIST',
  MODIFY_RESERVATION = 'MODIFY_RESERVATION'
}

export enum PaymentRequestType {
  THREE_D_SECURE = 'THREE_D_SECURE',
  THREE_D_VALIDATION = 'THREE_D_VALIDATION',
  PREAUTH = 'PREAUTH',
  COLLECT = 'COLLECT',
  CANCEL = 'CANCEL',
  REFUND = 'REFUND'
}

export const paymentStatusOptions = [
  { value: PaymentStatusType.COLLECTED, label: 'v.reservation.payment_status.collected' },
  { value: PaymentStatusType.PREAUTH, label: 'v.reservation.payment_status.preauth' },
  { value: PaymentStatusType.MPI_TOKEN, label: 'v.reservation.payment_status.mpi_token' },
  { value: PaymentStatusType.MPI_TOKEN_FAILED, label: 'v.reservation.payment_status.mpi_token_failed' },
  { value: PaymentStatusType.MPI_VALIDATED, label: 'v.reservation.payment_status.mpi_validated' },
  { value: PaymentStatusType.MPI_VALIDATION_FAILED, label: 'v.reservation.payment_status.mpi_validation_failed' },
  { value: PaymentStatusType.TOKEN, label: 'v.reservation.payment_status.token' },
  { value: PaymentStatusType.CANCELLED, label: 'v.reservation.payment_status.cancelled' },
  { value: PaymentStatusType.CANCELLATION_FAILED, label: 'v.reservation.payment_status.cancellation_failed' },
  { value: PaymentStatusType.COLLECTION_FAILED, label: 'v.reservation.payment_status.collection_failed' },
  { value: PaymentStatusType.PREAUTH_FAILED, label: 'v.reservation.payment_status.preauth_failed' },
  { value: PaymentStatusType.FAILED, label: 'v.reservation.payment_status.failed' },
  { value: PaymentStatusType.DECLINED, label: 'v.reservation.payment_status.declined' },
  { value: PaymentStatusType.REFUNDED, label: 'v.reservation.payment_status.refunded' },
  { value: PaymentStatusType.REFUND_FAILED, label: 'v.reservation.payment_status.refund_failed' }
];

export enum PaymentGatewayType {
  PAYPAL = 'PAYPAL',
  BORGUN = 'BORGUN',
  VALITOR = 'VALITOR'
}
